import { assertV2Namespace, V2_DATABASE_NAME } from './persistenceContract';

// ACCOUNT ISOLATION (Owner device test 2026-09-21: 「我新注册账号进去，结果里面显示了我前一个账号的验屋报告」).
//
// Until build 2.0.0 (92) the device had ONE database and ONE inspector profile, whoever was signed in.
// From here on everything an inspector records belongs to the account that recorded it:
//   - every account opens its OWN IndexedDB database (accountDatabaseName); the database the device
//     already had (V2_DATABASE_NAME) belongs to the account that adopts it once (v2Database.ts
//     resolveAccountDatabase) and to nobody else afterwards;
//   - the device conveniences that are really the inspector's (profile, remembered name) are stored
//     under an account-scoped key (scopedStorageKey);
//   - nothing is opened, read or written without an account once the app has asked for strictness
//     (requireAccountScope, first line of boot): a late write after sign-out is refused, never
//     redirected into somebody else's data.
//
// This module holds the ONE piece of state "which account is active" and nothing else — no store, no
// database handle — so the persistence layer and the preference keys can read it without a cycle.
let accountId: string | null = null;
let databaseName: string | null = null;
let strict = false;
// Counts every change of the active account (a leave, an entry). Never reset while the page lives.
let epoch = 0;

export class NoActiveAccountError extends Error {
  constructor(what: string) {
    super(`account scope: ${what} without a signed-in account`);
    this.name = 'NoActiveAccountError';
  }
}

// Called once, first thing at boot (src/main.tsx). Unit tests that exercise a store directly never
// call it and keep the unscoped names — exactly the names the device used before accounts were split.
export function requireAccountScope(): void {
  strict = true;
}

export function accountScopeRequired(): boolean {
  return strict;
}

// Account ids are Supabase user ids (UUIDs). Anything else (a development scenario id) is reduced to
// the characters a database name and a storage key carry safely.
function safeId(id: string): string {
  return id.trim().replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 80);
}

export function accountDatabaseName(id: string): string {
  const safe = safeId(id);
  if (safe.length === 0) throw new NoActiveAccountError('a database name');
  const name = `${V2_DATABASE_NAME}:${safe}`;
  assertV2Namespace(name);
  return name;
}

export function setActiveAccountScope(id: string | null, database: string | null): void {
  epoch += 1;
  if (id === null || database === null) {
    accountId = null;
    databaseName = null;
    return;
  }
  assertV2Namespace(database);
  accountId = safeId(id);
  databaseName = database;
}

export function activeAccountId(): string | null {
  return accountId;
}

// A SLOW operation (an AI request, a server round trip) takes this when it starts and asks it again
// before it writes or shows its result. False once the account it started under was left: between
// the leave and the next entry a write is refused anyway (activeDatabaseName), but an answer that
// arrives AFTER the next inspector signed in would find an open database — theirs. That answer
// belongs to nobody who is on the device now; it is dropped, never written into the next account.
export function accountGuard(): () => boolean {
  const startedIn = epoch;
  return () => epoch === startedIn;
}

// The database every persistence call opens. With no account: refused once strictness was asked for;
// the unscoped name otherwise (unit tests).
export function activeDatabaseName(): string {
  if (databaseName !== null) return databaseName;
  if (strict) throw new NoActiveAccountError('a database access');
  return V2_DATABASE_NAME;
}

// A localStorage key that belongs to the active account. With no account: the unscoped key outside
// strict mode (unit tests); inside strict mode there is nothing to read and nothing may be written,
// so callers get null and skip the access.
export function scopedStorageKey(baseKey: string): string | null {
  assertV2Namespace(baseKey);
  if (accountId !== null) return `${baseKey}:${accountId}`;
  return strict ? null : baseKey;
}

export function resetAccountScopeForTests(): void {
  accountId = null;
  databaseName = null;
  strict = false;
}
